import { useContext, useEffect } from "react";
import { useMediaQuery } from "react-responsive";
import { useNavigate, useParams } from "react-router-dom";

import {
  Card,
  Link as DSLink,
  Heading,
  Icon,
  Span,
} from "@icg360/design-system";

import { BaseLayout } from "components/base-layout";
import { Stack } from "components/common/stack";
import { MSSDrawer } from "components/mss-drawer";
import {
  type PolicyCoverageAndDeductibles,
  labelCoverages,
  policyTypeCoverageMap,
} from "components/renderers/coverage/policy-type-coverage-map";
import { InsuranceRepresentative } from "components/shared/insurance-representative";
import { MainLayout } from "components/shared/main-layout";
import { coverages, productTypeLabelMap, screenSizes } from "consts";
import {
  usePolicyDetail,
  usePolicyId,
  usePolicyPlatformId,
} from "data/ss-data-hooks";
import {
  formatCurrency,
  formatDate,
  getKeyDocs,
  matchesCarrierGroupOrFlood,
  scrollToTop,
  scrollToYPosition,
  trackEvent,
} from "utils";

const HIDE_COVERAGE_CARRIERS = ["vave", "markel"];

import { CommercialCoverages } from "./commercial-coverages";
import { CommercialDeductibles } from "./commercial-deductibles";
import { CoreCoverages } from "./core-coverages";
import ExtendedCoverages from "./extended-coverages";
import { CoverageContext } from "./extended-coverages/hooks";
import CoverageUpdate from "./extended-coverages/update-coverage";
import { useError } from "./extended-coverages/update-coverage/hooks";
import { KeyDeductibles } from "./key-deductibles";
import styles from "./policy.module.scss";

export const Policy = () => {
  const navigate = useNavigate();
  const ssPolicyId = usePolicyId();
  const policyPlatformId = usePolicyPlatformId();
  const { urlPolicyId } = useParams();
  const { data, isLoading } = usePolicyDetail();
  const { coveragePage, setCoveragePage } = useContext(CoverageContext);
  const errorState = useError();

  const { userDetails, userPolicyProperties } = data ?? {};
  const { currentTerm, isCommercial } = userDetails ?? { currentTerm: {} };
  const { carrierGroup, policyType } = userPolicyProperties ?? {};
  const hideCoverage = matchesCarrierGroupOrFlood(
    carrierGroup,
    policyType,
    HIDE_COVERAGE_CARRIERS,
  );
  const { latest } = data?.userDocuments ?? {};
  const activeCoverages = [
    {
      name: coverages.IDENTITY,
      premium: currentTerm?.identityFraudCoveragePremium,
      active: !!(currentTerm?.identityFraudCoveragePremium !== "0"),
    },
    {
      name: coverages.MECHANICAL,
      premium: currentTerm?.mechanicalBreakdownCoveragePremium,
      active: !!(currentTerm?.mechanicalBreakdownCoveragePremium !== "0"),
    },
    {
      name: coverages.SERVICE,
      premium: currentTerm?.serviceLinePremium,
      active: !!(currentTerm?.serviceLinePremium !== "0"),
    },
  ]?.filter((item) => item.active);

  const policyDeclarations = getKeyDocs(
    data?.userDocuments,
    (data?.userBilling?.mortgagees?.length ?? 0) > 0,
  ).filter((doc) => doc.name === "policyDeclarations")[0];

  // Give the coverages more descriptive names:
  const labelledCoverages = labelCoverages(currentTerm);
  // Generate which coverages to show based on policyType:
  const coverageMap: PolicyCoverageAndDeductibles =
    policyTypeCoverageMap[policyType ?? ""];

  const { deductibles, liabilityCoverages, propertyCoverages } =
    coverageMap ?? {};

  useEffect(() => {
    if (urlPolicyId !== ssPolicyId) {
      navigate(`/my/policies/${ssPolicyId}`);
      return;
    }
  }, [ssPolicyId, urlPolicyId, navigate]);

  useEffect(() => {
    if (!isLoading && hideCoverage) {
      navigate(`/my/policies/${ssPolicyId}/documents`, { replace: true });
    }
  }, [hideCoverage, isLoading, navigate, ssPolicyId]);

  useEffect(() => {
    trackEvent("Policy");
    scrollToTop();
  }, []);

  const isMobile = useMediaQuery({ maxWidth: screenSizes.laptop });

  useEffect(() => {
    if (coveragePage.scroll_to) scrollToYPosition(coveragePage.scroll_to);
  }, [coveragePage]);

  if (isLoading || hideCoverage) {
    return <BaseLayout loading />;
  }

  const declarationLink = () => {
    if (isCommercial) {
      if (latest?.declaration?.parentId && latest?.declaration?.location) {
        return `/api/policy/${latest?.declaration?.parentId}/${latest?.declaration?.location}`;
      }
      return "";
    }
    return `/api/policy/${policyDeclarations?.parentId}/${policyDeclarations?.location}`;
  };

  const hasDoc =
    policyDeclarations || (isCommercial && declarationLink() !== "");

  const CoreCoveragesHeader = () => (
    <div className={styles.coreCoveragesHeaderContainer}>
      <div className={styles.headerWrapper}>
        <Heading size="md">Core coverages</Heading>
      </div>
      {hasDoc && (
        <div className={styles.docLink}>
          <span>
            <Icon name="FileDoc" size="sm" />
          </span>{" "}
          <DSLink
            filename="Policy Declarations"
            type="PDF"
            name={
              isCommercial ? "Policy Declarations" : policyDeclarations?.label
            }
            href={declarationLink()}
            target="_blank"
            onPress={() => {
              trackEvent("Declaration Page Link Clicked", {
                sectionName: "Coverage Tab",
              });
            }}
          >
            View Declaration of Coverage
          </DSLink>
        </div>
      )}
    </div>
  );

  const PolicyDetailsView = () => (
    <Card>
      <Stack gap="xl">
        <Heading size="md">Policy details</Heading>
        <p>
          <Span color="quiet">Policy number</Span>
          <br /> <Span medium>#{policyPlatformId}</Span>
        </p>
        <p>
          <Span color="quiet">Policy type</Span>
          <br />{" "}
          <Span medium>
            {productTypeLabelMap[policyType ?? ""]} ({policyType})
          </Span>
        </p>
        <p>
          <Span color="quiet">Premium</Span>
          <br />{" "}
          <Span medium>{formatCurrency(currentTerm?.totalPremium, true)}</Span>
        </p>
        <p>
          <Span color="quiet">Term</Span>
          <br />{" "}
          <Span medium>
            {formatDate(currentTerm?.effectiveDatePolicyTerm)} -{" "}
            {formatDate(currentTerm?.expirationDate)}
          </Span>
        </p>
      </Stack>
    </Card>
  );

  const Main = () => (
    <Stack>
      {isMobile ? <PolicyDetailsView /> : null}
      <Card>
        <Stack gap="2xl">
          <CoreCoveragesHeader />
          {isCommercial ? (
            <CommercialCoverages data={data} />
          ) : (
            <CoreCoverages
              coveragesList={[...propertyCoverages, ...liabilityCoverages]}
              labelledCoverages={labelledCoverages}
            />
          )}
        </Stack>
      </Card>
      <Card>
        <Stack gap="2xl">
          <Heading size="md">Key deductibles</Heading>
          {isCommercial ? (
            <CommercialDeductibles data={data} />
          ) : (
            <KeyDeductibles
              deductibles={deductibles}
              labelledCoverages={labelledCoverages}
            />
          )}
        </Stack>
      </Card>
      <ExtendedCoverages activeCoverages={activeCoverages} />
    </Stack>
  );

  const Sidebar = () => (
    <Stack>
      {!isMobile ? <PolicyDetailsView /> : null}
      <InsuranceRepresentative />
    </Stack>
  );

  return (
    <>
      <BaseLayout
        tabs={[
          ...(hideCoverage
            ? []
            : [{ name: "Coverage", link: `/my/policies/${ssPolicyId}` }]),
          { name: "Documents", link: `/my/policies/${ssPolicyId}/documents` },
        ]}
        availableNotifications={[
          "ScheduledPayment",
          "HomeServices",
          "CancellationPayment",
        ]}
      >
        <MainLayout sidebar={<Sidebar />}>
          <Main />
        </MainLayout>
      </BaseLayout>
      <MSSDrawer
        isOpen={coveragePage.open}
        onClose={() => setCoveragePage({ y: 0, policyId: "", type: "close" })}
        showBackdrop
      >
        <MSSDrawer.Header
          title="Available extended coverages"
          onClose={() => setCoveragePage({ y: 0, policyId: "", type: "close" })}
        />
        {coveragePage.open && (
          <CoverageUpdate
            error={errorState.error}
            dispatchError={errorState.dispatchError}
            setCoveragePage={setCoveragePage}
            coveragePage={coveragePage}
          />
        )}
      </MSSDrawer>
    </>
  );
};
